import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { StatusProvider, useStatus } from './contexts/StatusProvider';
import { AuthProvider } from './contexts/AuthContext';
import { BrandProvider, useBrand } from './contexts/BrandContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import EquilateralDashboard from './pages/brands/EquilateralDashboard';
import SessionsPage from './pages/SessionsPage';
import AgentsPage from './pages/AgentsPage';
import SettingsPage from './pages/SettingsPage';
import ModelConfigPage from './pages/ModelConfigPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { WorkflowDesignerPage } from './pages/WorkflowDesigner/integration/WorkflowDesignerPage';
import { TopologyViewerPage } from './pages/TopologyViewer/TopologyViewerPage';
import BoardsPage from './pages/BoardsPage';
import IncidentsPage from './pages/IncidentsPage';
import StandardsPage from './pages/StandardsPage';
import StandardsProfilesPage from './pages/StandardsProfilesPage';
import StandardsReviewPage from './pages/StandardsReviewPage';
import StandardsBoard from './pages/StandardsBoard';
import SuperAdminDashboardPage from './pages/SuperAdminDashboardPage';
import ClientsPage from './pages/ClientsPage';
import UsersPage from './pages/UsersPage';
import OrganizationsPage from './pages/OrganizationsPage';
import RepositoriesPage from './pages/RepositoriesPage';
import AssessmentsPage from './pages/AssessmentsPage';
import CostDashboardPage from './pages/CostDashboardPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import BillingPage from './pages/BillingPage';
import CardEditorPage from './pages/CardEditorPage';
import TemplatesPage from './pages/TemplatesPage';
import TemplateViewer from './components/templates/TemplateViewer';
import DocumentEditor from './pages/DocumentEditor';
import ProjectsPage from './pages/ProjectsPage';
import ProjectConfigurationPage from './pages/ProjectConfigurationPage';
import SpecLibraryPage from './pages/SpecLibraryPage';
import TemplateLibraryPage from './pages/TemplateLibraryPage';
import Layout from './components/Layout';
import SuperAdminLayout from './components/SuperAdminLayout';
import EnterpriseOnboarding from './pages/onboarding/EnterpriseOnboarding';
import AssessmentReportPage from './pages/AssessmentReportPage';
import WorkflowInspectorPage from './pages/WorkflowInspector/WorkflowInspectorPage';
import { CrossEntityTopologyPage } from './pages/CrossEntityTopology/CrossEntityTopologyPage';
import { TouchpointRegistryPage } from './pages/CrossEntityTouchpoints/TouchpointRegistryPage';
import { CrossEntityWorkflowPage } from './pages/CrossEntityWorkflow/CrossEntityWorkflowPage';
import { FederatedDashboardPage } from './pages/FederatedDashboard/FederatedDashboardPage';
import MemoryDashboardPage from './pages/MemoryDashboardPage';
import InferenceDashboardPage from './pages/InferenceDashboardPage';
import GovernanceP3Page from './pages/GovernanceP3Page';
import AuditLogPage from './pages/AuditLogPage';

// Settings pages
import GitHubAppSetupPage from './pages/settings/GitHubAppSetupPage';
import PlatformCommitsPage from './pages/settings/PlatformCommitsPage';
import CommitApprovalQueue from './pages/settings/CommitApprovalQueue';

// Brand registry and pages
import { getBrandConfig, BRAND_REGISTRY } from './config/brandRegistry';
import GenericBrandLanding from './pages/brands/GenericBrandLanding';

/**
 * Brand-Aware Dashboard Router
 *
 * When a branded user (e.g. PowerSpec) hits /dashboard, this renders
 * their brand-specific dashboard instead of the generic Equilateral dashboard.
 * Falls back to EquilateralDashboard for unbranded or equilateral users.
 */
function BrandDashboardRouter() {
  const { brandCode } = useBrand();
  const brandConfig = getBrandConfig(brandCode);

  // If the brand has a custom dashboard, render it instead of the generic one
  if (brandConfig.dashboard && brandCode !== 'equilateral') {
    const DashboardComponent = brandConfig.dashboard;
    return <DashboardComponent />;
  }

  // Default: Equilateral dashboard
  return <EquilateralDashboard />;
}

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { Auth_Status, Is_Loading } = useStatus();

  if (Is_Loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Additional client-side token validation
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    try {
      // Decode JWT and check expiration
      const parts = accessToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

        // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          console.log('[ProtectedRoute] Token is expired, redirecting to login');
          localStorage.removeItem('idToken');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('user');
          localStorage.removeItem('selectedClientId');
          localStorage.removeItem('selectedCompanyId');
          return <Navigate to="/login" replace />;
        }
      }
    } catch (error) {
      console.error('[ProtectedRoute] Error validating token:', error);
      return <Navigate to="/login" replace />;
    }
  }

  if (!Auth_Status) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Root route handler that redirects based on user type
function RootRedirect() {
  const { Is_Super_Admin, Has_Client_Context, Needs_Onboarding } = useStatus();

  // New user without entitlements needs to complete onboarding
  if (Needs_Onboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Super admin without client context goes to super dashboard
  if (Is_Super_Admin && !Has_Client_Context) {
    return <Navigate to="/super/dashboard" replace />;
  }

  // Regular user or super admin with client context goes to regular dashboard
  return <Navigate to="/dashboard" replace />;
}

/**
 * Brand-Aware Root Handler
 *
 * Dynamically routes to brand-specific landing pages based on domain detection.
 * Uses BRAND_REGISTRY for configuration - add new brands there, not here.
 *
 * Flow:
 * 1. Detect brand from domain (via BrandContext)
 * 2. Look up brand config in registry
 * 3. If on app.* subdomain → treat as app (skip landing, require auth)
 * 4. If brand has public landing → show it (no auth)
 * 5. If brand has no landing or requires auth → redirect to login
 */
function BrandAwareRoot() {
  const { brandCode, isLoading } = useBrand();
  const { Auth_Status, Is_Loading: authLoading } = useStatus();

  // Wait for brand detection
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Get brand configuration from registry
  const brandConfig = getBrandConfig(brandCode);

  // Detect app.* subdomain — these are the authenticated app entry points
  // e.g., app.powerspec.ai → login/dashboard, NOT the marketing landing
  // The root domain (powerspec.ai) still shows the public landing page
  const hostname = window.location.hostname.toLowerCase();
  const isAppSubdomain = hostname.startsWith('app.');

  // Check if brand has a public landing page (only on root domain, not app.*)
  if (brandConfig.publicLanding && !isAppSubdomain) {
    // Use custom landing if available, otherwise generic
    const LandingComponent = brandConfig.landing || GenericBrandLanding;
    return <LandingComponent />;
  }

  // Brand requires authentication (or app.* subdomain)
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!Auth_Status) {
    return <Navigate to="/login" replace />;
  }

  return <RootRedirect />;
}

function AppRoutes() {
  return (
    <>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/onboarding" element={<EnterpriseOnboarding />} />
        {/* Public assessment page - linked from GitHub Issues */}
        <Route path="/assessment/:repoName" element={<AssessmentReportPage />} />
        <Route
          path="/"
          element={<BrandAwareRoot />}
        />
        <Route
          path="/super/dashboard"
          element={
            <ProtectedRoute>
              <SuperAdminLayout>
                <SuperAdminDashboardPage />
              </SuperAdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/clients"
          element={
            <ProtectedRoute>
              <SuperAdminLayout>
                <ClientsPage />
              </SuperAdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/users"
          element={
            <ProtectedRoute>
              <SuperAdminLayout>
                <UsersPage />
              </SuperAdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/super/organizations"
          element={
            <ProtectedRoute>
              <SuperAdminLayout>
                <OrganizationsPage />
              </SuperAdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <BrandDashboardRouter />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sessions"
          element={
            <ProtectedRoute>
              <Layout>
                <SessionsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents"
          element={
            <ProtectedRoute>
              <Layout>
                <AgentsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/model-config"
          element={
            <ProtectedRoute>
              <Layout>
                <ModelConfigPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workflow-designer"
          element={
            <ProtectedRoute>
              <Layout>
                <WorkflowDesignerPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/topology-viewer"
          element={
            <ProtectedRoute>
              <Layout>
                <TopologyViewerPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workflow-inspector"
          element={
            <ProtectedRoute>
              <Layout>
                <WorkflowInspectorPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/boards"
          element={
            <ProtectedRoute>
              <Layout>
                <BoardsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/incidents"
          element={
            <ProtectedRoute>
              <Layout>
                <IncidentsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/standards"
          element={
            <ProtectedRoute>
              <Layout>
                <StandardsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/standards/profiles"
          element={
            <ProtectedRoute>
              <Layout>
                <StandardsProfilesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews"
          element={
            <ProtectedRoute>
              <Layout>
                <StandardsReviewPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/standards-board"
          element={
            <ProtectedRoute>
              <Layout>
                <StandardsBoard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/repositories"
          element={
            <ProtectedRoute>
              <Layout>
                <RepositoriesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessments"
          element={
            <ProtectedRoute>
              <Layout>
                <AssessmentsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/costs"
          element={
            <ProtectedRoute>
              <Layout>
                <CostDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Layout>
                <AnalyticsDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Layout>
                <BillingPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Layout>
                <SettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/github-app"
          element={
            <ProtectedRoute>
              <Layout>
                <GitHubAppSetupPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/platform-commits"
          element={
            <ProtectedRoute>
              <Layout>
                <PlatformCommitsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/commit-approvals"
          element={
            <ProtectedRoute>
              <Layout>
                <CommitApprovalQueue />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <ProtectedRoute>
              <Layout>
                <TemplatesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/templates/:templateId"
          element={
            <ProtectedRoute>
              <Layout>
                <TemplateViewer />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cards/:cardId"
          element={<CardEditorPage />}
        />
        <Route
          path="/documents/:documentKey"
          element={
            <ProtectedRoute>
              <Layout>
                <DocumentEditor />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <Layout>
                <ProjectsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/configure"
          element={
            <ProtectedRoute>
              <Layout>
                <ProjectConfigurationPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Cross-Entity Governance Routes (EQ-PROV-007) */}
        <Route
          path="/governance/topology"
          element={
            <ProtectedRoute>
              <Layout>
                <CrossEntityTopologyPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/governance/touchpoints"
          element={
            <ProtectedRoute>
              <Layout>
                <TouchpointRegistryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/governance/workflow"
          element={
            <ProtectedRoute>
              <Layout>
                <CrossEntityWorkflowPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/governance/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <FederatedDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Governance P3 Dashboard (Tier Gating, Authority, Capsules, Standards Evolution) */}
        <Route
          path="/governance-p3"
          element={
            <ProtectedRoute>
              <Layout>
                <GovernanceP3Page />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Memory Dashboard (P3 Memory Backend) */}
        <Route
          path="/memory"
          element={
            <ProtectedRoute>
              <Layout>
                <MemoryDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Inference Dashboard (P3 Inference Router) */}
        <Route
          path="/inference"
          element={
            <ProtectedRoute>
              <Layout>
                <InferenceDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Audit Log */}
        <Route
          path="/audit-log"
          element={
            <ProtectedRoute>
              <Layout>
                <AuditLogPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Specification Library Routes */}
        <Route
          path="/specs"
          element={
            <ProtectedRoute>
              <Layout>
                <SpecLibraryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/specs/:specId"
          element={
            <ProtectedRoute>
              <Layout>
                <SpecLibraryPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Specification Template Library */}
        <Route
          path="/spec-templates"
          element={
            <ProtectedRoute>
              <Layout>
                <TemplateLibraryPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Dynamic Brand Routes - Generated from BRAND_REGISTRY */}
        {Object.entries(BRAND_REGISTRY).map(([brandCode, config]) => {
          const LandingComponent = config.landing || GenericBrandLanding;
          const DashboardComponent = config.dashboard;

          return (
            <React.Fragment key={brandCode}>
              {/* Brand landing page route */}
              <Route
                path={config.routePrefix}
                element={<LandingComponent />}
              />

              {/* Brand dashboard route (if dashboard exists) */}
              {DashboardComponent && (
                <Route
                  path={`${config.routePrefix}/dashboard`}
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <DashboardComponent />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              )}
            </React.Fragment>
          );
        })}
      </Routes>
    </>
  );
}

function App() {
  return (
    <StatusProvider>
      <AuthProvider>
        <BrandProvider>
          <EntitlementsProvider>
            <AppRoutes />
          </EntitlementsProvider>
        </BrandProvider>
      </AuthProvider>
    </StatusProvider>
  );
}

export default App;
